import React from 'react';
import classnames from 'classnames';
import { FieldArray, Formik } from 'formik';
import { useI18n } from '@whizz/react-i18n';
import type { ToggleModalProps } from '../../../../components/modal/types';
import type { ManageMetricsType } from '../../../../forms/types';
import type { MetricsSettingsType } from '../../../types';
import { Button, ChoiceGroup } from '../../../../components';
import { CancelButtonModal, ConfirmButtonModal } from '../../../../global';
import { MANAGE_METRICS_SCHEMA } from '../../../../forms/validations';
import { GLOBAL_ICONS } from '../../../../util/vars';
import {
  additional_metrics_settings,
  default_metrics_settings,
  school_additional_metrics_settings,
  school_default_metrics_setting,
} from '../../../types';
import { ModalFooter } from '../../../../components/modal/Modal';

export interface ManageMetricsModalProps extends Pick<ToggleModalProps, 'toggle'> {
  settings: ManageMetricsType;
  isSchool: boolean
  onSubmit(values: ManageMetricsType): Promise<void>
}

const ManageMetricsModal = ({ settings, isSchool = false, onSubmit, toggle }: ManageMetricsModalProps): JSX.Element => {

  const { translate } = useI18n();

  const getAdditionalSettings = () => {
    return isSchool
      ? school_additional_metrics_settings
      : additional_metrics_settings;
  };

  const SettingsCheckbox = ({ id, index, arrLength, checked, onChange }: {
    id: MetricsSettingsType,
    index: number,
    arrLength: number,
    checked: boolean,
    onChange(): void
  }) => {
    return (
      <div
        key={`metric_${id}`}
        className={classnames({ 'mb-md' : index + 1 !== arrLength })}
      >
        <ChoiceGroup
          id={`metric_${id}`}
          label={translate(`pages.REPORTS.METRICS.MODAL.SETTINGS.${id}`)}
          checked={checked}
          onChange={onChange}
        />
      </div>
    );
  };

  const metrics_settings = isSchool ? school_default_metrics_setting : default_metrics_settings;
  return (
    <div>
      <Formik
        initialValues={settings}
        validateOnMount
        validationSchema={MANAGE_METRICS_SCHEMA}
        onSubmit={(values) => {
          return Promise.resolve(onSubmit(values)).then(() => {
            toggle(false);
          });
        }}
      >
        {({ values, handleSubmit, setFieldValue, isValid, isSubmitting, isValidating }) => (
          <form onSubmit={handleSubmit} className={'mt-md'}>
            <div className='container'>
              <p className={'font-bold text-md'}>
                {translate('pages.REPORTS.METRICS.MODAL.DEFAULT')}
              </p>
              <FieldArray
                name={'default_settings'}
                render={(arrayHelpers) => (
                  <div className={'mt-md'}>
                    {metrics_settings.map((setting, index) => {
                      return (
                        <SettingsCheckbox
                          key={setting}
                          id={setting}
                          index={index}
                          arrLength={metrics_settings.length}
                          checked={values.default_settings.includes((setting))}
                          onChange={() => {
                            if (values.default_settings.includes(setting)) {
                              arrayHelpers.remove(values.default_settings.indexOf(setting));
                            } else {
                              arrayHelpers.push(setting);
                            }
                          }}
                        />
                      );
                    })}
                  </div>)}
              />
              <Button
                icon={GLOBAL_ICONS.refresh}
                isOutline
                color={'dark'}
                className={'mt-md mb-lg'}
                onClick={() => {
                  setFieldValue('default_settings', metrics_settings);
                  setFieldValue('additional_settings', []);
                }}
                data-cy="button-reset"
              >
                {translate('components.BUTTONS.RESET')}
              </Button>
              <p className={'font-bold text-md'}>
                {translate('pages.REPORTS.METRICS.MODAL.ADDITIONAL')}
              </p>
              <div className={'overflow-auto'} style={{ maxHeight: 200 }}>
                <FieldArray
                  name={'additional_settings'}
                  render={(arrayHelpers) => (
                    <div className={'mt-md'} data-cy="additional-metrics-container">
                      {getAdditionalSettings().map((setting, index) => (
                        <SettingsCheckbox
                          key={setting}
                          id={setting}
                          index={index}
                          arrLength={getAdditionalSettings().length}
                          checked={values.additional_settings.includes((setting))}
                          onChange={() => {
                            if (values.additional_settings.includes(setting)) {
                              arrayHelpers.remove(values.additional_settings.indexOf(setting));
                            } else {
                              arrayHelpers.push(setting);
                            }
                          }}
                        />
                      ))}
                    </div>)}
                />
              </div>
            </div>
            <ModalFooter>
              <CancelButtonModal
                type={'reset'}
                disabled={isValidating || isSubmitting}
                onClick={() => toggle(false)}
              />
              <ConfirmButtonModal
                type={'submit'}
                disabled={!isValid || isValidating || isSubmitting}
              >
                {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
              </ConfirmButtonModal>
            </ModalFooter>
          </form>
        )}
      </Formik>
    </div>
  );
};

ManageMetricsModal.displayName = 'ManageMetricsModal';

export default ManageMetricsModal;
